<template>
    <div v-if="shouldRender" class="slice-content-columns-line__grid" :class="featuredLineModifier" :data-testid="`columns-grid-line--${index}`">
        <div
            v-for="(column, columnIndex) in visibleColumns"
            :key="columnIndex"
            class="slice-content-columns-line__column"
            :data-testid="`slice-content-columns-column--${columnIndex}`"
        >
            <div
                v-if="isFilled.image(column.image)"
                class="slice-content-columns-line__image mui-background"
                :data-testid="`slice-content-columns-image--${columnIndex}`"
            >
                <div class="mui-shape"></div>
                <PrismicImage :field="column.image" :alt="column.image.alt" loading="lazy" sizes="(max-width: 767px) 85vw, (min-width: 768px) 33vw" />
            </div>
            <MuiTag v-if="isFilled.keyText(column.tag)">{{ column.tag }}</MuiTag>
            <div v-if="isFilled.keyText(column.title)">
                <h3 class="slice-content-columns-line__title" :data-testid="`slice-content-columns-title--${columnIndex}`">
                    {{ column.title }}
                </h3>
                <PrismicRichText
                    v-if="isFilled.richText(column.description)"
                    :field="column.description"
                    :class="[
                        'slice-content-columns-line__description',
                        {'slice-content-columns-line__description--with-checks': hasBulletPoints(column.description)},
                    ]"
                    :data-testid="`slice-content-columns-description--${columnIndex}`"
                />
            </div>
            <MuiLink
                v-if="isFilled.link(column.link) && column.link.url && linkText(column.link)"
                :href="column.link.url"
                :text="linkText(column.link) ?? ''"
                :target="(column.link as FilledLinkToWebField).target || undefined"
                class="slice-content-columns-line__link"
                :data-testid="`slice-content-columns-link--${columnIndex}`"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledLinkToWebField, ImageField, KeyTextField, LinkField, RichTextField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import {MuiLink, MuiTag} from '#components';

    type ContentColumnsGridLineColumn = {
        image: ImageField;
        title: KeyTextField;
        description: RichTextField;
        link: LinkField;
        tag?: KeyTextField;
    };

    interface CCGridLineProps {
        columns: ContentColumnsGridLineColumn[];
        index: number;
        columnsCountMin?: number;
        columnsCountMax?: number;
    }

    const {columns, index, columnsCountMin = 2, columnsCountMax = 3} = defineProps<CCGridLineProps>();

    const {columnCount, shouldRender, visibleColumns} = manageVisibleColumns({
        columns: () => columns,
        columnsCountMax: () => columnsCountMax,
        columnsCountMin: () => columnsCountMin,
    });

    const featuredLineModifier = computed(() =>
        index === 0 ? 'slice-content-columns-line--featured-first' : 'slice-content-columns-line--featured-last',
    );

    const {hasBulletPoints, linkText} = manageColumnContent();

    function manageVisibleColumns(args: {
        columns: () => ContentColumnsGridLineColumn[];
        columnsCountMax: () => number;
        columnsCountMin: () => number;
    }) {
        const visibleColumns = computed(() => args.columns().slice(0, args.columnsCountMax()));
        const shouldRender = computed(() => visibleColumns.value.length >= args.columnsCountMin());
        const columnCount = computed(() => visibleColumns.value.length);

        return {columnCount, shouldRender, visibleColumns};
    }

    function manageColumnContent() {
        function hasBulletPoints(field: RichTextField): boolean {
            if (!isFilled.richText(field)) {
                return false;
            }

            return field.some((block) => block.type === 'list-item');
        }

        function linkText(link: LinkField): string | undefined {
            if (!('text' in link) || typeof link.text !== 'string' || link.text.length === 0) {
                return undefined;
            }

            return link.text;
        }

        return {hasBulletPoints, linkText};
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-content-columns-line {
        &__grid {
            display: flex;
            gap: var(--joy-core-spacing-8);
            position: relative;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }

            @include mq.screen_sm(min) {
                display: grid;
                width: 100%;
                gap: 32px;
                grid-template-columns: repeat(v-bind(columnCount), minmax(0, 1fr));
            }

            @include mq.screen_md(min) {
                gap: 64px;
            }

            .mui-shape {
                display: none;
            }
        }

        &__column {
            padding-bottom: var(--joy-core-spacing-2);
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--joy-core-spacing-8);
            flex: 0 0 calc(100% - var(--joy-core-spacing-12));
            scroll-snap-align: start;
            min-width: 0;
            text-align: left;
            position: relative;

            &:not(:first-child)::after {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: -16px;
                border-left: 1px solid var(--joy-color-neutral-30);

                @include mq.screen_sm(min) {
                    left: -16px;
                }
            }

            @include mq.screen_md(min) {
                &:not(:first-child)::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: -32px;
                    border-left: 1px solid var(--joy-color-neutral-30);
                }
            }
        }

        &__link {
            margin-top: auto;
        }

        &__image {
            align-self: stretch;
            width: 100%;
            height: 180px;
            border-radius: var(--joy-core-radius-4);
            overflow: hidden;
            aspect-ratio: 16 / 9;
            padding: var(--joy-core-spacing-4);

            img {
                display: block;
                max-width: 100%;
                max-height: 100%;
                border-radius: var(--joy-core-radius-4);
                margin: 0 auto;
            }
        }

        &__title {
            margin: 0;
            font-family: var(--joy-font-family-title);
            font-size: var(--joy-font-size-secondary-200);
            font-weight: var(--joy-font-weight-bold);
            line-height: var(--joy-line-height-small);
            color: var(--joy-color-text-title);
            margin-bottom: var(--joy-core-spacing-4);
        }

        &__description {
            align-self: stretch;
            margin: 0;
            font-family: var(--joy-font-family-base);
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-neutral-40);

            p {
                margin: 0;
            }

            &--with-checks {
                ul {
                    padding: 0;
                    margin: 0;
                    list-style: none;

                    li {
                        list-style-type: none;
                        padding-left: var(--joy-core-spacing-7);
                        margin-bottom: var(--joy-core-spacing-3);
                        background-image: url('../check.svg');
                        background-repeat: no-repeat;
                        background-position: left center;
                        background-size: 15px 15px;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }

        &--with-single-line {
            border-top: 1px solid var(--joy-color-neutral-30);
            padding-top: var(--joy-core-spacing-12);
        }

        &--with-multiple-lines {
            padding: var(--joy-core-spacing-8) 0;

            &:not(:last-child) {
                border-bottom: 1px solid var(--joy-color-neutral-30);
            }

            @include mq.screen_md(min) {
                grid-template-columns: repeat(4, minmax(0, 1fr));

                &.slice-content-columns-line--featured-first > .slice-content-columns-line__column:first-child,
                &.slice-content-columns-line--featured-last > .slice-content-columns-line__column:last-child {
                    grid-column: span 2;

                    .slice-content-columns-line__image {
                        aspect-ratio: 32 / 9;
                        overflow: hidden;
                        position: relative;

                        img {
                            position: relative;
                        }
                    }

                    .mui-shape {
                        @include mq.screen_md(min) {
                            display: block;
                            right: calc(100% - 470px);
                        }
                    }
                }

                &.slice-content-columns-line--featured-last > .slice-content-columns-line__column:last-child {
                    .mui-shape {
                        right: calc(100% - 420px);
                    }
                }
            }
        }
    }
</style>
